import { makeStyles, TextField as MUITextField } from '@material-ui/core'
import { Save } from '@material-ui/icons'
import React, { useState } from 'react'
import { DateField, Labeled, TextField, UPDATE, useMutation } from 'react-admin'
import { DynamicPropertyDefinitionBaseDTO } from '../../core/dto/dynamic-property-definition.dto'
import { DynamicPropertyDataTypes } from '../../core/enum/DynamicPropertyDataTypes'
import Button from '../common/customized-mui-components/Button'
import { ResourceName } from '../../core/ResourceName'

const useStyles = makeStyles((theme) => ({
  edit: {
    padding: `${theme.spacing(1)}px ${theme.spacing(3)}px`,
    '& > div': {
      margin: theme.spacing(0.5),
    },
  },
  input: {
    marginBottom: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
}))

const DynamicPropertyField = ({
  dynamicProperty,
  record,
}: {
  dynamicProperty: DynamicPropertyDefinitionBaseDTO
  record: any
}) => {
  const { definitionOwnerType, key, type } = dynamicProperty

  const source = `${key?.charAt(0).toLowerCase()}${key?.substring(1)}`

  if (record?.[source])
    switch (type) {
      case DynamicPropertyDataTypes.STRING:
        return (
          <Labeled label={key}>
            <TextField
              source={source}
              resource={definitionOwnerType}
              record={record}
            />
          </Labeled>
        )
      case DynamicPropertyDataTypes.INTEGER:
        return (
          <Labeled label={key}>
            <TextField
              source={source}
              resource={definitionOwnerType}
              record={record}
            />
          </Labeled>
        )
      case DynamicPropertyDataTypes.DATE_TIME:
        return (
          <Labeled label={key}>
            <DateField
              source={source}
              resource={definitionOwnerType}
              record={record}
              showTime
            />
          </Labeled>
        )
      default:
        return <div />
    }

  return <div />
}

const DynamicPropertyShow = ({
  dynamicProperties,
  record,
}: {
  dynamicProperties?: DynamicPropertyDefinitionBaseDTO[]
  record: any
}) => (
  <>
    {dynamicProperties
      ? dynamicProperties.map(
          (dynamicProperty: DynamicPropertyDefinitionBaseDTO) => (
            <DynamicPropertyField
              dynamicProperty={dynamicProperty}
              key={dynamicProperty.id}
              record={record}
            />
          ),
        )
      : undefined}
  </>
)

const DynamicPropertyInput = ({
  dynamicProperty,
  handleSetValue,
  record,
}: {
  dynamicProperty: DynamicPropertyDefinitionBaseDTO
  handleSetValue: (source: string, value: any) => void
  record?: any
}) => {
  const { key, type } = dynamicProperty
  const classes = useStyles()

  const source = `${key?.charAt(0).toLowerCase()}${key?.substring(1)}`

  const handleChange = (e) => {
    handleSetValue(source, e?.target?.value)
  }

  switch (type) {
    case DynamicPropertyDataTypes.STRING:
      return (
        <MUITextField
          className={classes.input}
          defaultValue={record?.[source]}
          fullWidth
          label={dynamicProperty?.key}
          size="small"
          onChange={handleChange}
          type="text"
          variant="filled"
        />
      )
    case DynamicPropertyDataTypes.INTEGER:
      return (
        <MUITextField
          className={classes.input}
          defaultValue={record?.[source]}
          fullWidth
          label={dynamicProperty?.key}
          size="small"
          onChange={handleChange}
          type="number"
          variant="filled"
        />
      )
    case DynamicPropertyDataTypes.DATE_TIME:
      return (
        <MUITextField
          className={classes.input}
          defaultValue={record?.[source]}
          fullWidth
          label={dynamicProperty?.key}
          size="small"
          onChange={handleChange}
          type="datetime-local"
          variant="filled"
        />
      )
    default:
      return <div />
  }
}

const DynamicPropertyEdit = ({
  dynamicProperties,
  handleEditMode,
  record,
  refetch,
  resource,
}: {
  dynamicProperties?: DynamicPropertyDefinitionBaseDTO[]
  handleEditMode: () => void
  record: any
  refetch: () => void
  resource: ResourceName
}) => {
  const classes = useStyles()
  const [mutate] = useMutation()
  const [values, setValues] = useState(record)

  const handleSetValue = (source: string, value: any) => {
    setValues({
      ...record,
      ...values,
      [source]: value,
    })
  }

  const handleSave = () => {
    mutate({
      type: UPDATE,
      resource,
      payload: {
        data: values,
        id: record?.id,
      },
    })
  }

  const handleClick = async () => {
    await handleSave()
    handleEditMode()
    refetch()
  }

  return (
    <div className={classes.edit}>
      {dynamicProperties
        ? dynamicProperties.map(
            (dynamicProperty: DynamicPropertyDefinitionBaseDTO) => (
              <DynamicPropertyInput
                dynamicProperty={dynamicProperty}
                handleSetValue={handleSetValue}
                record={record}
              />
            ),
          )
        : undefined}
      <div>
        <Button
          label="ra.action.save"
          onClick={handleClick}
          startIcon={<Save />}
          variant="contained"
        />
      </div>
    </div>
  )
}

export const DynamicPropertyView = ({
  dynamicProperties,
  editMode,
  handleEditMode,
  record,
  refetch,
  resource,
}: {
  dynamicProperties?: DynamicPropertyDefinitionBaseDTO[]
  editMode?: boolean
  handleEditMode: () => void
  record: any
  refetch: () => void
  resource: ResourceName
}) => {
  if (!editMode)
    return (
      <DynamicPropertyShow
        dynamicProperties={dynamicProperties}
        record={record}
      />
    )

  return (
    <DynamicPropertyEdit
      dynamicProperties={dynamicProperties}
      handleEditMode={handleEditMode}
      record={record}
      refetch={refetch}
      resource={resource}
    />
  )
}
